import React, { Component } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Card from '@material-ui/core/Card';
import PlaceholderSpinner from 'components/kea/placeholders/PlaceholderSpinner';
import * as actions from 'actions/Recs';
import { Breadcrumbs } from 'components/ContainerHeader';
import FeaturePageShell from 'components/shell/FeaturePageShell';
import SettingsToggleSwitcher from 'components/kea/settings/SettingsToggleSwitcher';
import SettingTextInput from 'components/kea/settings/SettingTextInput';
import SettingRangeSlider from 'components/kea/settings/SettingRangeSlider';
import SettingsSelectInput from 'components/kea/settings/SettingsSelectInput';
import IntlMessages from 'util/IntlMessages';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import { objectDifference } from 'util/appUtils';

class Widget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      widget: {},
      widgetDefault: {},
    };

    this.changeHandler = (value, key) => {
      const { widget } = this.state;
      this.setState({ widget: { ...widget, [key]: value } });
    };

    this.discardChanges = () => {
      this.setState({ widget: this.state.widgetDefault });
    };
  }

  componentDidMount() {
    const { settings, recs: { model, widget, page } } = this.props;
    if (!model.isFetched) actions.recsLoadAllModels(settings.shopId);
    if (!widget.isFetched) actions.recsLoadAllWidgets(settings.shopId);
    if (!page.isFetched) actions.recsLoadAllPages(settings.shopId);
  }

  static getDerivedStateFromProps(props, state) {
    const {
      match: { params: { widgetId } },
      recs: { widget: widgets },
    } = props;

    if (widgets.isFetched && widgets.dataMap[widgetId]) {
      const currentWidget = widgets.dataMap[widgetId];
      const stateShouldBeUpdated = _.isEqual(state.widgetDefault, currentWidget) === false;
      if (stateShouldBeUpdated) {
        return { widget: currentWidget, widgetDefault: currentWidget };
      }
    }
    return null;
  }

  detectNewWidget({ sections }, { sections: sectionsDef }) {
    const widgets = _.flatten(sections.map((section) => section.widgets));
    const widgetsDef = _.flatten(sectionsDef.map((section) => section.widgets));

    return _.difference(widgets, widgetsDef);
  }

  render() {
    const { match, recs, settings } = this.props;
    const { params: { pageId, widgetId } } = match;
    const { model: models, widget: widgets, page } = recs;

    const { widget, widgetDefault } = this.state;

    const ableToRender = !settings.isFetched || !models.isFetched || !widgets.isFetched || !page.isFetched;
    if (ableToRender) return <PlaceholderSpinner />;

    const widgetSettingsIsNotChanged = () => {
      const diff = objectDifference(widget, widgetDefault);
      return Object.keys(diff).length === 0;
    };

    const thisWidgetIsNew = () => {
      const widgetsDiff = this.detectNewWidget(page.dataMap[pageId], page.dataMapDefault[pageId]);
      return widgetsDiff.includes(widgetId);
    };

    const disableSaveBtn = () => widgetSettingsIsNotChanged() && !thisWidgetIsNew();

    const saveActionHandler = (pageId) => {
      const { widget } = this.state;
      if (!widgetSettingsIsNotChanged()) this.props.actions.recsUpdateOneWidget(widget, pageId);
      if (thisWidgetIsNew()) this.props.actions.recsUpdatePage(page.dataMap[pageId]);
    };

    const widgetMap = _.keyBy(widgets.data, 'alias');
    const currentWidget = widgetMap[widgetId];

    const buttons = [
      { text: <IntlMessages id="common.save" />, action: () => saveActionHandler(pageId), active: true, disabled: disableSaveBtn(), color: 'primary' },
      { text: <IntlMessages id="common.discard" />, action: this.discardChanges, active: true, disabled: widgetSettingsIsNotChanged(), color: 'secondary' },
    ];

    return (
      <FeaturePageShell
        width="wide"
        secondaryActions={<Breadcrumbs url={match.url} show />}
        title={<IntlMessages id="recommendations.customization.widget.edit.title" values={{ widgetName: currentWidget.title }} />}
        actions={(
          <SaveActionBtnGrp
            isFetching={widgets.isUpdating}
            buttons={buttons}
          />
        )}
      >
        <Card className="jr-card-full jr-card-tabs-right jr-card-profile jr-card jr-card-widget ">
          <div className="settings-container">

            <div className="setting-group-header"><IntlMessages id="ui.customization.widgets.pageEditor.widget.widgetSettings" /></div>
            <SettingsToggleSwitcher
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.showTitle" />}
              value={widget.showTitle}
              onChange={(value) => this.changeHandler(value, 'showTitle')}
            />

            <SettingTextInput
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.title" />}
              value={widget.title}
              onChange={(value) => this.changeHandler(value, 'title')}
            />

            <SettingRangeSlider
              value={widget.maxItemsCount}
              onChange={(value) => this.changeHandler(value, 'maxItemsCount')}
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.maximalItemsCount" />}
              min={2}
              max={16}
              step={1}
            />

            <SettingsToggleSwitcher
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.showAddToCartButton" />}
              value={widget.showAddToCartButton}
              onChange={(value) => this.changeHandler(value, 'showAddToCartButton')}
            />

            <SettingsToggleSwitcher
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.forceShowWidget" />}
              value={widget.showIfLittleRecs}
              onChange={(value) => this.changeHandler(value, 'showIfLittleRecs')}
              description={<IntlMessages id="ui.customization.widgets.pageEditor.widget.showTheWidgetEvenWhenTheAmountOfRecommendationsIs" />}
            />

            <div className="setting-group-header"><IntlMessages id="ui.customization.widgets.pageEditor.widget.widgetBehaviour" /></div>

            <SettingsSelectInput
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.algorithmOfWidgetBehaviour" />}
              items={models.dataForSelect}
              value={widget.model}
              onChange={(value) => this.changeHandler(value, 'model')}
            />

            <SettingsToggleSwitcher
              title={<IntlMessages id="ui.customization.widgets.pageEditor.widget.shuffleItems" />}
              value={widget.shuffle}
              onChange={(value) => this.changeHandler(value, 'shuffle')}
            />
          </div>
        </Card>
      </FeaturePageShell>
    );
  }
}

export default connect(
  (state) => ({
    recs: state.recs,
    settings: state.settings,
  }),
  (dispatch) => ({
    actions: bindActionCreators(actions, dispatch),
  }),
)(Widget);
